// This file is shared across multiple parts of the product, so should only import
// constants and types, or other "lightweight" code.
import { type CallhomeCamera } from '@solinkcorp/api-types';
import settings from '@solinkcorp/settings';
import { GLOBAL_CAMERA_TAGS, VIDEO_ASPECT_HEIGHT, VIDEO_ASPECT_WIDTH } from 'vd-constants';

/**
 * Returns true if a camera is a 360-degree camera.
 * Based on the condition that either the tag from CH or the camera name contains 360.
 */
export function is360Camera(camera: { name: string; cameraTagIds?: readonly string[] }): boolean {
  return /360/.test(camera.name) || !!camera.cameraTagIds?.includes(GLOBAL_CAMERA_TAGS.THREE_SIXTY);
}

/**
 * Returns true if a camera is a public view monitor (PVM).
 * Tag only: an analog PVM behind an encoder reports the encoder's model, not its own.
 */
export function isPvmCamera(camera: { cameraTagIds?: CallhomeCamera['cameraTagIds'] }): boolean {
  return !!camera.cameraTagIds?.includes(GLOBAL_CAMERA_TAGS.PVM);
}

/**
 * Returns true if a camera has reported PTZ capabilities via Callhome
 * (`camera.ptzCapabilities` is non-null). This reflects hardware capability only —
 * see `isPtzEnabledCamera`/`CameraUtil.isPtzActive` for whether the user has also
 * turned PTZ controls on for this camera.
 */
export function isPtzCamera(camera: { name: string; ptzCapabilities?: unknown | null }): boolean {
  return Boolean(camera.ptzCapabilities);
}

/**
 * Returns the dimension of the first stream.
 */
export function getStreamAspectRatio(streams: CallhomeCamera['streams']): {
  width: number;
  height: number;
  aspectRatio: number;
  isPortrait: boolean;
} {
  // You would think we'd want to use the `playingStream` here to get the aspect ratio
  // instead of an arbitrary stream, so we can get the correct aspect ratio if
  // it's available...  But, unfortunately, the aspect ratio we compute here is sometimes
  // wrong for SD streams (possibly for HD streams too, but the examples I've seen
  // have all been SD streams).  If you look at e.g. "Drive Thru Window Interior @ Store 101600"
  // in McCluskey, you'll see the camera's SD stream has a resolution of 640x480,
  // suggesting this should be a 4:3 aspect ratio.  However, if you actually try
  // to display the stream it renders at 16:9.  If you grab a .ts file from the
  // stream and run it through `ffmpeg -i`, you'll see the resolution listed
  // as `640x480 [SAR 126:95 DAR 168:95]` - here `SAR` is the Source Aspect Ratio
  // (4:3) and `DAR` is the `Display Aspect Ratio` (16:9).  We don't have easy
  // access to this data here, though.  So, we grab an arbirtrary stream, and
  // hope it is the correct aspect ratio for both streams (it probably isn't,
  // but it's what we have).
  //
  // We also have some weird cases like Chick-Fil-A, "Carraway Village FSU (04263)"
  // which has some cameras with broken HD streams (see, for example,
  // "Employee Entrance", or "Dumpster").  These streams have a bitrate of 0,
  // and connect reports their dimensions as -1x-1.  Trying to use -1x-1 to
  // figure out the dimensions of the video player results in terrible things
  // happening, so if `streams[0]` is such a stream, we use `streams[1]` instead.
  //
  // Finally, if we can't find any streams with sensible data, we just use
  // an arbitrary width and height, and hope it works out.

  const streamToUse = streams.find(
    (stream) => stream.dimensions && stream.dimensions.width > 0 && stream.dimensions.height > 0,
  );

  const { width, height } = streamToUse?.dimensions || {
    width: VIDEO_ASPECT_WIDTH,
    height: VIDEO_ASPECT_HEIGHT,
  };
  const aspectRatio = width / height;
  return {
    width,
    height,
    aspectRatio,
    isPortrait: aspectRatio <= 1,
  };
}

export function getCloudFrontUrl(tenantId: string, jwt: string, url: string): string {
  return `${settings.images_url}/${tenantId}/${url}?token=${jwt}`;
}
